import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, tap, throwError } from 'rxjs';

import { AuthService } from '@services/auth.service';
import { ToastService } from '@services/toast.service';

export type HttpToastPolicy = 'none' | 'errors' | 'success' | 'all';

export const HTTP_TOAST_POLICY = new HttpContextToken<HttpToastPolicy>(() => 'errors');

interface ApiResponseInfo {
  statusCode?: number;
  message?: string;
}

function getApiResponseInfo(body: unknown): ApiResponseInfo {
  if (typeof body !== 'object' || body === null) {
    return {};
  }

  const response = body as Record<string, unknown>;
  return {
    statusCode: typeof response['statusCode'] === 'number' ? response['statusCode'] : undefined,
    message: typeof response['message'] === 'string' ? response['message'] : undefined,
  };
}

function isApiRequest(url: string, apiUrl: string): boolean {
  const apiRoot = new URL(apiUrl);
  const requestUrl = new URL(url, apiRoot);
  const rootPath = apiRoot.pathname.replace(/\/+$/, '');

  return requestUrl.origin === apiRoot.origin &&
    (requestUrl.pathname === rootPath || requestUrl.pathname.startsWith(`${rootPath}/`));
}

function isPublicAuthRequest(url: string, apiUrl: string): boolean {
  const requestUrl = new URL(url, apiUrl);
  return /\/users\/(login|request-password-reset|process-password-reset|register|activate-user)\/?$/i
    .test(requestUrl.pathname);
}

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const toastService = inject(ToastService);
  const toastPolicy = request.context.get(HTTP_TOAST_POLICY);
  const apiRequest = isApiRequest(request.url, authService.apiUrl);
  const publicAuthRequest = apiRequest && isPublicAuthRequest(request.url, authService.apiUrl);
  const token = authService.getToken()?.trim();
  const bearerToken = token?.replace(/^Bearer\s+/i, '').trim();
  const requestHadAuth = !!bearerToken || request.headers.has('Authorization');

  const authenticatedRequest = apiRequest && !publicAuthRequest && bearerToken
    ? request.clone({ setHeaders: { Authorization: `Bearer ${bearerToken}` } })
    : request;

  const handleFailure = (status: number, message?: string): void => {
    if (status === 401 && requestHadAuth && !publicAuthRequest && authService.loggedIn()) {
      authService.logout();
      if (toastPolicy === 'errors' || toastPolicy === 'all') {
        toastService.error('Your session has expired. Please sign in again.');
      }
      void router.navigate(['/auth/login'], { queryParams: { returnUrl: router.url } });
      return;
    }

    if (
      status === 403 &&
      requestHadAuth &&
      !publicAuthRequest &&
      !router.url.startsWith('/forbidden')
    ) {
      void router.navigateByUrl('/forbidden');
    }

    if (toastPolicy === 'errors' || toastPolicy === 'all') {
      const userMessage = status === 401 && requestHadAuth && !publicAuthRequest
        ? 'Your session has expired. Please sign in again.'
        : status === 403
          ? 'You do not have permission to perform this action.'
          : status === 0
            ? 'Unable to connect. Check your connection and try again.'
            : status >= 500
              ? 'The service is temporarily unavailable. Please try again later.'
              : message || `The request could not be completed (${status}).`;
      toastService.error(userMessage);
    }
  };

  return next(authenticatedRequest).pipe(
    tap((event) => {
      if (!(event instanceof HttpResponse)) {
        return;
      }

      const responseInfo = getApiResponseInfo(event.body);
      if (responseInfo.statusCode !== undefined && responseInfo.statusCode >= 400) {
        handleFailure(responseInfo.statusCode, responseInfo.message);
        return;
      }

      if ((toastPolicy === 'success' || toastPolicy === 'all') && responseInfo.message?.trim()) {
        toastService.success(responseInfo.message);
      }
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        const responseInfo = getApiResponseInfo(error.error);
        handleFailure(error.status, responseInfo.message);
      } else {
        handleFailure(0);
      }

      return throwError(() => error);
    }),
  );
};
